import { Link, createFileRoute } from '@tanstack/react-router'

export const Route = createFileRoute('/')({ component: App })

const kicker =
  'font-mono text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-blue-700 dark:text-blue-400'

const btnPrimary =
  'inline-flex items-center gap-2 rounded-lg bg-blue-700 px-5 py-2.5 text-sm font-semibold text-white no-underline transition hover:bg-blue-800'

const btnGhost =
  'inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 no-underline transition hover:border-blue-700 hover:text-blue-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-blue-400 dark:hover:text-blue-400'

function LockerCell({
  id,
  wide,
  led,
}: {
  id: string
  wide?: boolean
  led?: boolean
}) {
  return (
    <div
      className={`relative rounded-md border border-slate-300 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60 ${
        wide ? 'col-span-2' : ''
      }`}
    >
      <span className="absolute left-2 top-1.5 font-mono text-[0.55rem] tracking-wider text-slate-400 dark:text-slate-500">
        {id}
      </span>
      <span
        className={`absolute right-2 top-2 h-1.5 w-1.5 rounded-full ${
          led
            ? 'bg-blue-600 shadow-[0_0_6px] shadow-blue-500 motion-safe:animate-led dark:bg-blue-400'
            : 'bg-slate-300 dark:bg-slate-600'
        }`}
      />
      <span className="absolute bottom-2 left-1/2 h-1 w-4 -translate-x-1/2 rounded-full bg-slate-300 dark:bg-slate-600" />
    </div>
  )
}

function LockerWall() {
  return (
    <figure className="m-0 w-full max-w-md">
      <div
        aria-hidden="true"
        className="rounded-2xl border border-slate-200 bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.06),0_10px_28px_rgba(15,23,42,0.05)] dark:border-slate-800 dark:bg-slate-900"
      >
        <div className="flex items-center justify-between px-1 pb-3 font-mono text-[0.6rem] font-medium uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
          <span>Lugstash · Wall 04</span>
          <span className="flex items-center gap-1.5">
            <span className="h-1 w-1 rounded-full bg-blue-600 dark:bg-blue-400" />
            Online
          </span>
        </div>

        <div className="grid auto-rows-[3.4rem] grid-cols-5 gap-1.5">
          <LockerCell id="A1" />
          <LockerCell id="A2" />

          {/* payment terminal column */}
          <div className="row-span-4 flex flex-col items-center gap-2.5 rounded-md border border-slate-300 bg-slate-800 p-2 dark:border-slate-700 dark:bg-slate-950">
            <div className="h-9 w-full rounded bg-gradient-to-br from-blue-600 to-blue-900" />
            <div
              className="h-7 w-7"
              style={{
                backgroundImage:
                  'radial-gradient(circle, rgba(255,255,255,0.45) 1.5px, transparent 2px)',
                backgroundSize: '9px 9px',
              }}
            />
            <div className="mb-1 mt-auto h-0.5 w-3/5 rounded-full bg-white/30" />
          </div>

          <LockerCell id="A3" led />
          <LockerCell id="A4" />
          <LockerCell id="B1" wide />
          <LockerCell id="B2" />
          <LockerCell id="B3" led />
          <LockerCell id="C1" />
          <LockerCell id="C2" led />

          {/* open cell with parcel */}
          <div className="relative col-span-2 row-span-2 rounded-md border border-blue-600 bg-blue-50 ring-1 ring-inset ring-blue-600 dark:border-blue-400 dark:bg-blue-400/10 dark:ring-blue-400">
            <span className="absolute left-2 top-1.5 font-mono text-[0.55rem] tracking-wider text-blue-700 dark:text-blue-300">
              C3 · OPEN
            </span>
            <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-blue-600 shadow-[0_0_6px] shadow-blue-500 motion-safe:animate-led dark:bg-blue-400" />
            <div className="absolute bottom-3 left-1/2 h-2/5 w-3/5 -translate-x-1/2 rounded-sm bg-[#c89e6a] after:absolute after:inset-y-0 after:left-1/2 after:w-[7%] after:-translate-x-1/2 after:bg-white/30 dark:bg-[#a07b4c] dark:after:bg-white/20" />
          </div>

          <LockerCell id="D1" wide />
        </div>
      </div>
      <figcaption className="mt-3 text-center font-mono text-[0.62rem] uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
        Cell C3 open for pickup
      </figcaption>
    </figure>
  )
}

const steps = [
  {
    num: '01',
    title: 'Courier drops off',
    body: 'A one-time code opens the right-size cell. The wall confirms the parcel is inside before the door locks.',
  },
  {
    num: '02',
    title: 'Customer gets a code',
    body: 'The pickup code arrives by SMS or e-mail the moment the door shuts. Paid deliveries settle by card at the terminal.',
  },
  {
    num: '03',
    title: 'Cell frees itself',
    body: 'After pickup the cell reports empty, self-checks its lock, and goes straight back into rotation.',
  },
]

const hardware = [
  {
    title: 'Modular cells',
    body: 'Six sizes on one rail — reconfigure a wall on site in minutes, no tools beyond a hex key.',
  },
  {
    title: 'Works offline',
    body: 'A local controller keeps doors opening and codes validating even when the network does not.',
  },
  {
    title: 'Built for outdoors',
    body: 'Powder-coated steel, IP54 sealing, and a working range of −20 °C to +45 °C.',
  },
]

const platform = [
  {
    title: 'Live fleet view',
    body: 'Every wall, cell, door, and sensor on one screen — with faults surfaced before customers notice.',
  },
  {
    title: 'Firmware over the air',
    body: 'Stage an update on one wall, roll it out to the fleet, roll it back if anything looks wrong.',
  },
  {
    title: 'Payments and audit built in',
    body: 'Card and QR payments per cell, and a complete audit trail of every door that ever opened.',
  },
]

const specs = [
  ['Cell sizes', '6 · XS to XL'],
  ['Capacity', '12–96 cells per wall'],
  ['Connectivity', 'LTE + Ethernet, offline fallback'],
  ['Power', '230 V · 60 W typical'],
  ['Climate', 'IP54 · −20 °C to +45 °C'],
  ['Integration', 'REST API + webhooks'],
]

function App() {
  return (
    <main className="mx-auto w-full max-w-6xl px-4 pb-8 pt-14 sm:px-6">
      {/* Hero */}
      <section className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="motion-safe:animate-rise-in">
          <p className={`${kicker} mb-4`}>Smart parcel lockers</p>
          <h1 className="mb-5 max-w-xl text-4xl font-bold leading-[1.05] tracking-tight text-slate-900 font-stretch-[112%] sm:text-6xl dark:text-white">
            The locker wall that runs itself.
          </h1>
          <p className="mb-8 max-w-lg text-base leading-7 text-slate-600 sm:text-lg dark:text-slate-400">
            Lugstash pairs modular steel lockers with a fleet console that
            watches every cell — deliveries, payments, firmware, and faults,
            handled from one screen.
          </p>
          <div className="mb-8 flex flex-wrap gap-3">
            <a href="mailto:sales@lugstash.example" className={btnPrimary}>
              Request a demo
            </a>
            <a href="#how-it-works" className={btnGhost}>
              See how it works
            </a>
          </div>
          <p className="font-mono text-[0.65rem] uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
            IP54 rated · −20 °C to +45 °C · LTE + Ethernet · 6 cell sizes
          </p>
        </div>

        <div
          className="flex justify-center motion-safe:animate-rise-in lg:justify-end"
          style={{ animationDelay: '120ms' }}
        >
          <LockerWall />
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="mt-24 scroll-mt-24">
        <p className={`${kicker} mb-3`}>From drop-off to pickup</p>
        <h2 className="mb-10 max-w-2xl text-3xl font-bold tracking-tight text-slate-900 font-stretch-[112%] sm:text-4xl dark:text-white">
          Three steps, no counter staff.
        </h2>
        <ol className="m-0 grid list-none gap-6 p-0 sm:grid-cols-3">
          {steps.map((step) => (
            <li key={step.num}>
              <div className="flex items-center gap-3 font-mono text-xs font-semibold tracking-[0.12em] text-blue-700 dark:text-blue-400">
                {step.num}
                <span className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
              </div>
              <h3 className="mb-2 mt-4 text-base font-semibold text-slate-900 dark:text-white">
                {step.title}
              </h3>
              <p className="m-0 text-sm leading-6 text-slate-600 dark:text-slate-400">
                {step.body}
              </p>
            </li>
          ))}
        </ol>
      </section>

      {/* System: hardware + platform */}
      <section id="platform" className="mt-24 scroll-mt-24">
        <p className={`${kicker} mb-3`}>One system</p>
        <h2 className="mb-10 max-w-2xl text-3xl font-bold tracking-tight text-slate-900 font-stretch-[112%] sm:text-4xl dark:text-white">
          Steel outside, software inside.
        </h2>
        <div className="grid gap-6 lg:grid-cols-2">
          {(
            [
              ['Hardware', hardware],
              ['Platform', platform],
            ] as const
          ).map(([label, items]) => (
            <div
              key={label}
              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,0.06)] sm:p-8 dark:border-slate-800 dark:bg-slate-900"
            >
              <p className="mb-6 font-mono text-[0.62rem] font-semibold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
                {label}
              </p>
              <ul className="m-0 list-none space-y-6 p-0">
                {items.map((item) => (
                  <li key={item.title} className="flex gap-3.5">
                    <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-sm bg-blue-700 dark:bg-blue-400" />
                    <div>
                      <h3 className="m-0 mb-1 text-base font-semibold text-slate-900 dark:text-white">
                        {item.title}
                      </h3>
                      <p className="m-0 text-sm leading-6 text-slate-600 dark:text-slate-400">
                        {item.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-slate-600 dark:text-slate-400">
          Already operate a Lugstash fleet?{' '}
          <Link
            to="/login"
            className="font-semibold text-blue-700 no-underline hover:underline dark:text-blue-400"
          >
            Sign in to the console →
          </Link>
        </p>
      </section>

      {/* Specs */}
      <section className="mt-24">
        <div className="rounded-2xl border border-slate-200 bg-slate-100 p-6 sm:p-8 dark:border-slate-800 dark:bg-slate-900/60">
          <p className="mb-6 font-mono text-[0.62rem] font-semibold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
            Technical specifications
          </p>
          <dl className="m-0 grid gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
            {specs.map(([key, value]) => (
              <div key={key}>
                <dt className="mb-1 font-mono text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
                  {key}
                </dt>
                <dd className="m-0 font-mono text-sm text-slate-800 dark:text-slate-200">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* CTA */}
      <section className="mt-24">
        <div className="rounded-2xl bg-blue-700 px-6 py-12 text-center sm:px-10">
          <h2 className="mb-3 text-3xl font-bold tracking-tight text-white font-stretch-[112%] sm:text-4xl">
            Put a wall to work.
          </h2>
          <p className="mx-auto mb-8 max-w-md text-base leading-7 text-blue-100">
            Tell us where your parcels pile up — we&apos;ll spec the wall, the
            cells, and the rollout.
          </p>
          <a
            href="mailto:sales@lugstash.example"
            className="inline-flex items-center rounded-lg bg-white px-6 py-3 text-sm font-semibold text-blue-700 no-underline transition hover:text-blue-900"
          >
            Request a demo
          </a>
        </div>
      </section>
    </main>
  )
}
